import { useEffect } from 'react';
import { App as CapApp } from '@capacitor/app';
import { refreshSession } from '@features/auth/api';
import { queryClient } from '@shared/lib/queryClient';
import { platform } from '@shared/lib/platform';

/**
 * Minimum gap between refreshes. App-switching fires resume constantly; a real
 * absence — long enough for something to have changed server-side — is longer.
 */
const MIN_REFRESH_GAP_MS = 30_000;

let lastRefreshAt = 0;

/**
 * Keep an open session current. Mobile sessions run for weeks, so everything
 * read once at sign-in — permissions, profile, the communities list — goes
 * stale in place; before this, a member approved into a community mid-session
 * could only reach it by signing out and back in.
 *
 * Refreshes on every return to the foreground (native resume, web tab focus),
 * and invalidates the query cache so screens left mounted across the absence
 * refetch instead of repainting what they held before it.
 *
 * Mounted once by the authenticated shell.
 */
export function useSessionRefresh(): void {
  useEffect(() => {
    const refreshIfDue = () => {
      const now = Date.now();
      if (now - lastRefreshAt < MIN_REFRESH_GAP_MS) return;
      lastRefreshAt = now;
      refreshSession();
      void queryClient.invalidateQueries();
    };

    if (platform.isNative()) {
      let cancelled = false;
      let remove: (() => void) | undefined;
      void CapApp.addListener('appStateChange', ({ isActive }) => {
        if (isActive) refreshIfDue();
      }).then((handle) => {
        // Cleanup may have run before the plugin handed back the handle.
        if (cancelled) void handle.remove();
        else remove = () => void handle.remove();
      });
      return () => {
        cancelled = true;
        remove?.();
      };
    }

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') refreshIfDue();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);
}
